import React, { useState } from 'react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Card from '../components/common/Card';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import ErrorModal from '../components/modals/ErrorModal';
import SuccessModal from '../components/modals/SuccessModal';
import { formatFileSize } from '../utils/helpers';

const S3Upload = () => {
  const [fileLocation, setFileLocation] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadMethod, setUploadMethod] = useState('presigned'); // Only presigned method
  const [useCustomFilename, setUseCustomFilename] = useState(false);
  const [customFilename, setCustomFilename] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  const handleFileLocationChange = (e) => {
    setFileLocation(e.target.value);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleCustomFilenameChange = (e) => {
    setCustomFilename(e.target.value);
  };

  const handleUseCustomFilenameChange = (e) => {
    setUseCustomFilename(e.target.checked);
    if (!e.target.checked) {
      setCustomFilename('');
    }
  };


  const uploadFilePresigned = async (file) => {
    // First, get presigned URL
    const presignedResponse = await fetch('/api/s3/presigned-url', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileName: file.name,
        fileType: file.type,
        fileLocation: fileLocation,
        useCustomFilename: useCustomFilename,
        customFilename: customFilename
      }),
    });

    const presignedData = await presignedResponse.json();
    
    if (!presignedData.success) {
      throw new Error(presignedData.message);
    }

    // Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(presignedData.data.presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.statusText}`);
    }

    return {
      success: true,
      message: 'File uploaded successfully!',
      data: {
        url: `https://${presignedData.data.bucket}.s3.${presignedData.data.region}.amazonaws.com/${presignedData.data.s3Key}`,
        key: presignedData.data.s3Key,
        bucket: presignedData.data.bucket,
        region: presignedData.data.region,
        size: file.size,
        type: file.type
      }
    };
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setModalData({
        title: "No Files Selected",
        message: "Please select at least one file to upload.",
        details: "Click the 'Choose Files' button to select files from your computer."
      });
      setShowErrorModal(true);
      return;
    }

    setIsUploading(true);
    setUploadResults([]);
    setShowErrorModal(false);
    setShowSuccessModal(false);

    const results = [];
    let successCount = 0;
    let errorCount = 0;

    for (const file of selectedFiles) {
      try {
        const result = await uploadFilePresigned(file);
        
        results.push({
          file: file.name,
          success: true,
          result: result
        });
        successCount++;
      } catch (error) {
        results.push({
          file: file.name,
          success: false,
          error: error.message
        });
        errorCount++;
      }
    }

    setUploadResults(results);
    setIsUploading(false);

    // Show appropriate modal based on results
    if (successCount > 0 && errorCount === 0) {
      setModalData({
        title: "Upload Successful!",
        message: `All ${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully to S3.`,
        details: results.map(r => ({
          "File": r.file,
          "Status": r.success ? "✅ Success" : "❌ Failed",
          "URL": r.success ? r.result.data.url : "N/A"
        }))
      });
      setShowSuccessModal(true);
    } else if (successCount > 0 && errorCount > 0) {
      setModalData({
        title: "Partial Upload Success",
        message: `${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully, ${errorCount} failed.`,
        details: results.map(r => ({
          "File": r.file,
          "Status": r.success ? "✅ Success" : "❌ Failed",
          "Error": r.success ? "N/A" : r.error
        }))
      });
      setShowSuccessModal(true);
    } else {
      setModalData({
        title: "Upload Failed",
        message: "All file uploads failed. Please check your configuration and try again.",
        details: results.map(r => ({
          "File": r.file,
          "Error": r.error
        }))
      });
      setShowErrorModal(true);
    }
  };


  return (
    <div style={styles.container}>
      <LoadingOverlay isVisible={isUploading} message="Uploading files to S3..." />
      
      <Card 
        title="S3 File Upload"
        subtitle="Upload files directly to AWS S3 with custom file location paths."
        style={styles.content}
      >

        <div style={styles.form}>
          <div style={styles.formGroup}>
            <Input
              label="File Location Path"
              value={fileLocation}
              onChange={handleFileLocationChange}
              placeholder="e.g., documents, images/photos, temp/uploads"
              helpText={`Optional: Specify a folder path in S3. Files will be uploaded to: ${fileLocation || 'uploads'}/${useCustomFilename && customFilename ? `${customFilename}.ext` : 'timestamp_filename'}`}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={useCustomFilename}
                onChange={handleUseCustomFilenameChange}
                style={styles.checkbox}
              />
              Use Custom Filename
            </label>
            <small style={styles.helpText}>
              When enabled, you can specify a custom filename (without extension)
            </small>
          </div>

          {useCustomFilename && (
            <div style={styles.formGroup}>
              <Input
                label="Custom Filename (No Extension)"
                value={customFilename}
                onChange={handleCustomFilenameChange}
                placeholder="e.g., my-document, report-2024, profile-photo"
                helpText="Enter filename without extension. Original file extension will be preserved automatically."
              />
            </div>
          )}


          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="fileInput">
              Choose Files
            </label>
            <input
              type="file"
              id="fileInput"
              multiple
              onChange={handleFileSelect}
              style={styles.fileInput}
            />
            {selectedFiles.length > 0 && (
              <div style={styles.fileList}>
                <h4>Selected Files ({selectedFiles.length}):</h4>
                {selectedFiles.map((file, index) => (
                  <div key={index} style={styles.fileItem}>
                    <span style={styles.fileName}>{file.name}</span>
                    <span style={styles.fileSize}>({formatFileSize(file.size)})</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button
            onClick={handleUpload}
            disabled={isUploading || selectedFiles.length === 0}
            style={{ width: '100%', marginTop: '20px' }}
          >
            {isUploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
          </Button>
        </div>

        {uploadResults.length > 0 && (
          <div style={styles.resultsSection}>
            <h3>Upload Results</h3>
            {uploadResults.map((result, index) => (
              <div key={index} style={{
                ...styles.resultCard,
                ...(result.success ? styles.successCard : styles.errorCard)
              }}>
                <div style={styles.resultHeader}>
                  <strong>{result.file}</strong>
                  <span style={styles.resultStatus}>
                    {result.success ? '✅ Success' : '❌ Failed'}
                  </span>
                </div>
                
                {result.success ? (
                  <div style={styles.successDetails}>
                    <p><strong>URL:</strong> <a href={result.result.data.url} target="_blank" rel="noopener noreferrer">{result.result.data.url}</a></p>
                    <p><strong>S3 Key:</strong> {result.result.data.key}</p>
                    <p><strong>Bucket:</strong> {result.result.data.bucket}</p>
                    <p><strong>Size:</strong> {formatFileSize(result.result.data.size)}</p>
                    <p><strong>Type:</strong> {result.result.data.type}</p>
                  </div>
                ) : (
                  <div style={styles.errorDetails}>
                    <p><strong>Error:</strong> {result.error}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={styles.infoSection}>
          <h3>Configuration Info</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <strong>Upload Method:</strong> Presigned URL (Direct to S3)
            </div>
            <div style={styles.infoItem}>
              <strong>File Location:</strong> {fileLocation || 'uploads'}
            </div>
            <div style={styles.infoItem}>
              <strong>Max File Size:</strong> 5GB (Direct to S3)
            </div>
            <div style={styles.infoItem}>
              <strong>Supported:</strong> All file types
            </div>
          </div>
        </div>
      </Card>

      {/* Modals */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={modalData?.title}
        message={modalData?.message}
        details={modalData?.details}
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={modalData?.title}
        message={modalData?.message}
        details={modalData?.details}
      />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc',
    padding: '20px',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
    background: 'white',
    borderRadius: '20px',
    padding: '40px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: '20px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '1.1rem',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '40px',
    textAlign: 'center',
  },
  form: {
    marginBottom: '40px',
  },
  formGroup: {
    marginBottom: '25px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 'bold',
    color: '#333',
    fontSize: '1rem',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #e1e5e9',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'border-color 0.3s ease',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #e1e5e9',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: 'white',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  fileInput: {
    width: '100%',
    padding: '12px 16px',
    border: '2px dashed #e1e5e9',
    borderRadius: '8px',
    fontSize: '1rem',
    cursor: 'pointer',
    backgroundColor: '#f8f9fa',
    boxSizing: 'border-box',
  },
  helpText: {
    display: 'block',
    marginTop: '5px',
    color: '#666',
    fontSize: '0.9rem',
    lineHeight: '1.4',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    fontWeight: 'bold',
    color: '#333',
    fontSize: '1rem',
    cursor: 'pointer',
  },
  checkbox: {
    marginRight: '10px',
    transform: 'scale(1.2)',
    cursor: 'pointer',
  },
  fileList: {
    marginTop: '15px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e1e5e9',
  },
  fileItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #e1e5e9',
  },
  fileName: {
    fontWeight: '500',
    color: '#333',
  },
  fileSize: {
    color: '#666',
    fontSize: '0.9rem',
  },
  uploadButton: {
    width: '100%',
    padding: '15px 30px',
    background: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '50px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'transform 0.3s ease',
  },
  uploadButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none',
  },
  resultsSection: {
    marginBottom: '40px',
  },
  resultCard: {
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '15px',
    border: '1px solid',
  },
  successCard: {
    background: '#d4edda',
    borderColor: '#c3e6cb',
    color: '#155724',
  },
  errorCard: {
    background: '#f8d7da',
    borderColor: '#f5c6cb',
    color: '#721c24',
  },
  resultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  resultStatus: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
  },
  successDetails: {
    fontSize: '0.9rem',
    lineHeight: '1.6',
  },
  errorDetails: {
    fontSize: '0.9rem',
    lineHeight: '1.6',
  },
  infoSection: {
    background: '#f8f9fa',
    padding: '20px',
    borderRadius: '10px',
    marginTop: '30px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginTop: '15px',
  },
  infoItem: {
    padding: '10px',
    background: 'white',
    borderRadius: '5px',
    fontSize: '0.9rem',
  },
};

export default S3Upload;
